import { PhrasingContent, Root, Text } from "mdast";
import { Node } from "unist";

// ---- Types ----
export interface ComponentMetadata<Props extends object> {
  // The default component to render for interactive display
  defaultComponent: React.ComponentType<Props>;
  // The props that are passed to the component from the markdown
  markdownPropKeys: (keyof Props)[];
  // Additional props that are passed to the component from the parent
  additionalPropValues?: Partial<Props>;
  // The component to render when the component tag is incomplete during streaming
  placeholder?: React.ComponentType;
  // The component to render when the component tag is invalid
  errorFallback?: React.ComponentType;
  // Optional converters to convert the component to a string for markdown display
  converters?: Record<string, (props: Props) => string>;
  // Additional components to render in different contexts
  modes?: Record<string, React.ComponentType<Props>>;
}

// Type-safe registry entry creator that enforces prop types
export function createRegistryEntry<Props extends object>({
  defaultComponent,
  markdownPropKeys,
  additionalPropValues,
  placeholder,
  errorFallback,
  converters,
  modes,
}: {
  defaultComponent: React.ComponentType<Props>;
  markdownPropKeys: (keyof Props)[];
  additionalPropValues?: Partial<Props>;
  placeholder?: React.ComponentType;
  errorFallback?: React.ComponentType;
  converters?: Record<string, (props: Props) => string>;
  modes?: Record<string, React.ComponentType<Props>>;
}): ComponentMetadata<Props> {
  return {
    defaultComponent,
    markdownPropKeys,
    additionalPropValues,
    placeholder,
    errorFallback,
    converters,
    modes,
  };
}

// Type-safe registry builder with strict type checking
export function createComponentRegistry<
  T extends Record<string, ComponentMetadata<any>>,
>(entries: T): T {
  return entries;
}

export type ComponentRegistry = Record<string, ComponentMetadata<any>>;

const JSON_ANNOTATION = "@@json@@:";

// ---- Attribute Parsing Utils ----

/**
 * Parse HTML/XML attribute string into key-value pairs
 * Handles double quotes, single quotes, unquoted values, and boolean attributes
 *
 * @param attributesStr - The attribute string (e.g., 'name="value" enabled count="42"')
 * @returns Object with parsed attributes as strings
 *
 * @example
 * parseAttributes('name="John" age="30" enabled')
 * // Returns: { name: "John", age: "30", enabled: true }
 */
export function parseAttributes(attributesStr: string): Record<string, any> {
  const attributes: Record<string, any> = {};

  if (attributesStr) {
    const attrRegex = /(\w+)(?:=(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;
    let attrMatch;
    while ((attrMatch = attrRegex.exec(attributesStr)) !== null) {
      const [, key, doubleQuoted, singleQuoted, unquoted] = attrMatch;
      attributes[key] = doubleQuoted ?? singleQuoted ?? unquoted ?? true;
    }
  }

  return attributes;
}

// ---- Utils ----
function parseAnnotatedProps(
  flat: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(flat)) {
    if (typeof value === "string" && value.startsWith(JSON_ANNOTATION)) {
      try {
        result[key] = JSON.parse(value.slice(JSON_ANNOTATION.length));
      } catch {
        result[key] = value;
      }
    } else {
      result[key] = value;
    }
  }
  return result;
}

export function mergeMarkdownProps<LProps extends object>(
  markdownProps: Record<string, unknown>,
  additionalProps?: Partial<LProps>
): LProps {
  const parsedMarkdownProps = parseAnnotatedProps(markdownProps);
  return {
    ...additionalProps,
    ...parsedMarkdownProps,
  } as LProps;
}

/**
 * Custom MDAST node for remark plugins
 */
export interface CustomNode extends Node {
  type: string;
  data?: {
    hName: string;
    hProperties?: Record<string, any>;
  };
  children?: PhrasingContent[];
}

/**
 * Validates that a remark visit context is safe to process
 * Returns true if the node, index, and parent are all valid for text processing
 */
export const isValidTextNodeContext = (
  node: any,
  index: number | undefined,
  parent: any
): boolean => {
  return (
    typeof index === "number" &&
    !!parent &&
    node?.type === "text" &&
    typeof node.value === "string"
  );
};

/**
 * Creates a text node for use in remark plugins
 */
export const createTextNode = (value: string): Text => ({
  type: "text",
  value,
});

/**
 * Creates a custom element node for use in remark plugins
 * This creates a custom MDAST node that react-markdown can render via components
 */
export const createCustomElement = <T extends Record<string, any>>(
  tagName: string,
  properties?: T,
  children?: PhrasingContent[]
): CustomNode => ({
  type: tagName,
  data: {
    hName: tagName,
    hProperties: properties || {},
  },
  children: children || [],
});

/**
 * Result type for text replacement that can optionally remove characters from surrounding text
 */
export interface ReplacementResult {
  node: CustomNode | Text;
  removePrecedingChars?: number;
  removeFollowingChars?: number;
}

/**
 * Processes text matches and replaces them with custom elements
 */
export const processTextMatches = (
  nodeValue: string,
  matches: RegExpMatchArray[],
  createReplacementNode: (
    match: RegExpMatchArray,
    index: number
  ) => ReplacementResult
): PhrasingContent[] => {
  const newChildren: PhrasingContent[] = [];
  let lastIndex = 0;

  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const [fullMatch] = match;
    const matchIndex = match.index!;

    // Get the replacement result
    const replacementResult = createReplacementNode(match, i);

    const node = replacementResult.node;
    const removePrecedingChars = replacementResult.removePrecedingChars || 0;
    const removeFollowingChars = replacementResult.removeFollowingChars || 0;

    // Calculate adjusted boundaries
    const adjustedStartIndex = Math.max(0, matchIndex - removePrecedingChars);
    const adjustedEndIndex = Math.min(
      nodeValue.length,
      matchIndex + fullMatch.length + removeFollowingChars
    );

    // Add text before the match (accounting for preceding char removal)
    if (adjustedStartIndex > lastIndex) {
      newChildren.push(
        createTextNode(nodeValue.slice(lastIndex, adjustedStartIndex))
      );
    }

    // Add the replacement node
    newChildren.push(node as PhrasingContent);

    lastIndex = adjustedEndIndex;
  }

  // Add remaining text after the last match
  if (lastIndex < nodeValue.length) {
    newChildren.push(createTextNode(nodeValue.slice(lastIndex)));
  }

  return newChildren;
};

/**
 * Finds the parent table row of a given node in MDAST
 * @param node - The node to find the parent table row for
 * @param parentMap - A map of nodes to their parent nodes
 * @returns The parent table row element or null if no table row is found
 */
export function findParentTableRow(
  node: Node,
  parentMap: WeakMap<Node, Node>
): any | null {
  let current: Node | undefined = node;
  while (current) {
    const parent = parentMap.get(current);
    if (!parent) break;
    if (parent.type === "tableRow") {
      return parent;
    }
    current = parent;
  }
  return null;
}

/**
 * Extracts plain text from a table row element in MDAST
 * @param row - The table row element to extract text from
 * @returns The extracted plain text
 */
export function extractPlainTextFromTableRow(row: any): string {
  const extractTextFromNode = (node: any): string => {
    if (node.type === "text") return node.value || "";
    if (node.children && Array.isArray(node.children)) {
      return node.children.map(extractTextFromNode).join("");
    }
    return "";
  };

  if (row.type !== "tableRow" || !row.children) return "";

  return row.children
    .filter((cell: any) => cell.type === "tableCell")
    .map((cell: any) => extractTextFromNode(cell).trim())
    .filter((text: string) => text.length > 0)
    .join(" | ");
}

// ---- New Traversal Utilities ----

/**
 * Callback type for text node visitors
 */
export type TextVisitorCallback = (
  node: any,
  index: number,
  parent: any
) => number | void;

/**
 * Callback type for general node visitors
 */
export type NodeVisitorCallback = (
  node: any,
  index: number,
  parent: any
) => void;

/**
 * Creates a text visitor function that handles tree modifications properly
 * Returns a function that can be called with (tree, callback) to traverse text nodes
 */
export const createTextVisitor = () => {
  return (tree: Root, callback: TextVisitorCallback) => {
    const visit = (node: any, parent: any, index: number) => {
      if (node.type === "text" && node.value) {
        const newIndex = callback(node, index, parent);
        if (typeof newIndex === "number") {
          return newIndex;
        }
      }

      if (node.children) {
        for (let i = 0; i < node.children.length; i++) {
          const newIndex = visit(node.children[i], node, i);
          if (typeof newIndex === "number") {
            i = newIndex;
          }
        }
      }
    };

    visit(tree, null, 0);
  };
};

/**
 * Creates a node visitor function for general node traversal
 * Returns a function that can be called with (tree, callback) to traverse all nodes
 */
export const createNodeVisitor = () => {
  return (tree: Root, callback: NodeVisitorCallback) => {
    const visit = (node: any, parent: any, index: number) => {
      callback(node, index, parent);

      if (node.children) {
        for (let i = 0; i < node.children.length; i++) {
          visit(node.children[i], node, i);
        }
      }
    };

    visit(tree, null, 0);
  };
};

/**
 * Simple text processing helper that handles regex matching and replacement
 */
export const processTextWithRegex = (
  node: any,
  index: number,
  parent: any,
  regex: RegExp,
  createReplacementNode: (
    match: RegExpMatchArray,
    index: number
  ) => ReplacementResult
): number | void => {
  if (!isValidTextNodeContext(node, index, parent)) {
    return;
  }

  const matches = Array.from(node.value.matchAll(regex)) as RegExpMatchArray[];
  if (matches.length === 0) return;

  const newChildren = processTextMatches(
    node.value,
    matches,
    createReplacementNode
  );

  // Replace the text node with the new children
  if (parent && parent.children && Array.isArray(parent.children)) {
    parent.children.splice(index, 1, ...newChildren);
    return index + newChildren.length - 1;
  }
};

// Compile regexes once at plugin level
export const getIncompleteTagPattern = (registryTags: string[]) =>
  registryTags.map((tag) => `<${tag}(?!(?:\\s[^>]*)?\\s*\\/>)[^<]*`).join("|");

// Match self-closing tags while respecting quoted attributes.
// Pattern matches <tag attr="value" /> where quoted values can contain special chars like >
// (?:[^"'>]|"[^"]*"|'[^']*')* matches either non-special chars OR complete quoted strings
export const getCompleteTagPattern = (registryTags: string[]) =>
  registryTags
    .map((tag) => `<${tag}(?:\\s(?:[^"'>]|"[^"]*"|'[^']*')*)?\\s*\\/>`)
    .join("|");

export const processMarkdownWithConverter = (
  markdown: string,
  registry: ComponentRegistry,
  converter: string
) => {
  const registryTags = Object.keys(registry);
  const completeTagPattern = getCompleteTagPattern(registryTags);
  const regex = new RegExp(`(${completeTagPattern})`, "g");

  return markdown.replace(regex, (match) => {
    // Parse the complete tag to extract tag name and attributes
    const { tagName, attributes } = parseCompleteTag(match);

    const registryEntry = registry[tagName];
    if (!registryEntry) {
      return match; // Return unchanged if tag not found in registry
    }

    const converterFn = registryEntry.converters?.[converter];
    if (!converterFn) {
      return match; // Return unchanged if converter not found
    }

    // Extract props using markdownPropKeys from the parsed attributes
    const extractedProps = extractPropsFromAttributes(
      attributes,
      registryEntry.markdownPropKeys
    );

    // Merge with additional prop values from registry
    const mergedProps = {
      ...registryEntry.additionalPropValues,
      ...extractedProps,
    };

    // Call converter with the merged props
    try {
      return converterFn(mergedProps);
    } catch (error) {
      console.error(`Error calling converter for ${tagName}:`, error);
      return match;
    }
  });
};

// Helper function to parse a complete tag and extract tag name and attributes
const parseCompleteTag = (tagString: string) => {
  // Match pattern like: <tag_name attr1="value1" attr2="value2" /> (self-closing only)
  const tagMatch = tagString.match(/^<(\w+)([^>]*?)\s*\/>$/);

  if (!tagMatch) {
    return { tagName: "", attributes: {} };
  }

  const tagName = tagMatch[1];
  const attributesString = tagMatch[2];

  // Use shared parseAttributes utility
  const attributes = parseAttributes(attributesString);

  return { tagName, attributes };
};

// Helper function to extract props using markdownPropKeys
const extractPropsFromAttributes = (
  attributes: Record<string, string>,
  markdownPropKeys: any
) => {
  const extractedProps: Record<string, any> = {};

  // Filter and ensure we only work with string keys
  const stringKeys = Array.isArray(markdownPropKeys)
    ? markdownPropKeys.filter(
        (key: any): key is string => typeof key === "string"
      )
    : [];

  stringKeys.forEach((propKey: string) => {
    if (attributes[propKey]) {
      extractedProps[propKey] = attributes[propKey];
    }
  });

  // Use existing parseAnnotatedProps function to handle @@json@@: format
  // Only values with @@json@@: annotation will be parsed as JSON
  return parseAnnotatedProps(extractedProps);
};
